// MARK://dhk8/admin-shell/pages/modules/InventoryPage
// Módulo 8 — Inventario → dhk8-erp-inventory (REST + mock).
//
// DS RBAC (Gustavo 2026-06-22): la LISTA usa DataTable + FilterBar + SummaryStrip
// con StatusPill por estado (agotado=danger, bajo=warn, ok=ok). El ajuste de stock
// que antes era +/−1 inline se reemplaza por un único modal "Ajustar a N" (fijar la
// existencia exacta; el delta = nuevo − actual se sigue mandando a adjustStock, así
// la matemática de stock no cambia). Borrar usa ConfirmDialog; el feedback va por Toast.
//
// Bug #91 (intacto): el alta exige warehouseId + (productId XOR ingredientName) — el
// form los recoge con dropdowns y muestra los errores del backend en un banner.
import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Trash2, Warehouse as WarehouseIcon, History, CalendarClock, AlertTriangle, Calculator } from 'lucide-react'
import * as inv from '../../lib/clients/inventory.js'
import * as cat from '../../lib/clients/catalog.js'
import type { InventoryItem } from '../../lib/mock-data.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { mxn } from '../../lib/money.js'
import Sheet from '../../components/Sheet.js'
import MoneyInput from '../../components/MoneyInput.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import UnitPicker, { unitShort } from '../../components/UnitPicker.js'
import DataTable, { type DataTableColumn, type SortState } from '../../components/DataTable.js'
import FilterBar from '../../components/FilterBar.js'
import { SummaryStrip } from '../../components/StatCard.js'
import StatusPill, { type StatusTone } from '../../components/StatusPill.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import { useToast } from '../../components/Toast.js'

const STATUS_TONE: Record<string, StatusTone> = { OK: 'ok', LOW: 'warn', OUT: 'danger' }
const STATUS_LABEL: Record<string, string> = { OK: 'OK', LOW: 'Bajo', OUT: 'Agotado' }

// Cantidad mono, alineada a la derecha (tabular). Reutilizada en varias columnas.
function Qty({ value, unit }: { value: number; unit?: string }) {
  return <span className="adm-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}{unit ? ` ${unitShort(unit)}` : ''}</span>
}

// B / CANON §1.12 (D4): el insumo es un Product del catálogo (productId) O una
// materia prima con nombre libre (ingredientName) — XOR. `source` controla cuál.
interface CreateForm {
  source: 'product' | 'free'
  productId: string
  ingredientName: string
  warehouseId: string
  unit: string
  reorderLevel: number
  unitCostCents: number
}
const emptyCreate = (): CreateForm => ({ source: 'product', productId: '', ingredientName: '', warehouseId: '', unit: 'pza', reorderLevel: 0, unitCostCents: 0 })

export default function InventoryPage() {
  const confirm = useConfirm()
  const { toast } = useToast()
  const navigate = useNavigate()
  const list = useAsync((s) => inv.listInventory(s))
  const warehouses = useAsync((s) => inv.listWarehouses(s))
  const products = useAsync((s) => cat.listProducts(s))
  // Pull INGREDIENT rows so we can flag the ones tagged `needs-attention:cost-0`
  // (insumos sin precio del onboarding — venían con #DIV/0! en el Excel).
  const ingredients = useAsync((s) => cat.listIngredients(s))

  const [creating, setCreating] = useState(false)
  const [createForm, setCreateForm] = useState<CreateForm>(emptyCreate())

  // Ajuste exacto: fija la existencia a un valor arbitrario (delta = nuevo − actual).
  const [adjustingItem, setAdjustingItem] = useState<InventoryItem | null>(null)
  const [adjustQty, setAdjustQty] = useState(0)

  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Warehouse creation (empty-state CTA)
  const [creatingWh, setCreatingWh] = useState(false)
  const [whForm, setWhForm] = useState<{ name: string; code: string }>({ name: '', code: '' })
  const [whError, setWhError] = useState<string | null>(null)
  const [savingWh, setSavingWh] = useState(false)

  // ── presentación: búsqueda + filtro de almacén + orden (cliente) ──
  const [search, setSearch] = useState('')
  const [whFilter, setWhFilter] = useState('')
  const [sort, setSort] = useState<SortState | undefined>(undefined)

  const whName = (id: string | null) => (id ? warehouses.data?.find((w) => w.id === id)?.name ?? '—' : '—')

  function openCreate() {
    setCreateForm({ ...emptyCreate(), warehouseId: warehouses.data?.[0]?.id ?? '' })
    setFormError(null); setCreating(true)
  }

  async function saveCreate() {
    setFormError(null)
    if (!createForm.warehouseId) { setFormError('Selecciona un almacén.'); return }
    if (createForm.source === 'product' && !createForm.productId) { setFormError('Selecciona un producto.'); return }
    if (createForm.source === 'free' && !createForm.ingredientName.trim()) { setFormError('Escribe el nombre del insumo.'); return }
    if (!createForm.unit.trim()) { setFormError('La unidad es obligatoria.'); return }
    setSaving(true)
    try {
      await inv.createItemWithRefs({
        warehouseId: createForm.warehouseId,
        unit: createForm.unit,
        reorderLevel: createForm.reorderLevel,
        unitCostCents: createForm.unitCostCents,
        ...(createForm.source === 'product' ? { productId: createForm.productId } : { ingredientName: createForm.ingredientName.trim() }),
      })
      setCreating(false); list.reload(); toast('Insumo creado', 'success')
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'No se pudo crear el insumo.')
    } finally { setSaving(false) }
  }

  async function remove(it: InventoryItem) {
    const ok = await confirm({ title: `¿Eliminar "${it.name}"?`, message: 'El insumo se quitará del inventario. No se puede deshacer.', tone: 'danger', confirmLabel: 'Eliminar' })
    if (!ok) return
    try { await inv.deleteItem(it.id); list.reload(); toast('Insumo eliminado', 'success') }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar el insumo.', 'danger') }
  }

  function openAdjust(it: InventoryItem) { setAdjustingItem(it); setAdjustQty(it.onHand); setFormError(null) }
  async function saveAdjust() {
    if (!adjustingItem) return
    // delta = nuevo − actual: NO tocamos la matemática de stock, solo fijamos el destino.
    const delta = Math.round((adjustQty - adjustingItem.onHand) * 100) / 100
    if (delta === 0) { setAdjustingItem(null); return }
    setFormError(null); setSaving(true)
    try { await inv.adjustStock(adjustingItem.id, delta); setAdjustingItem(null); list.reload(); toast('Existencia ajustada', 'success') }
    catch (e) { setFormError(e instanceof Error ? e.message : 'No se pudo ajustar la existencia.') }
    finally { setSaving(false) }
  }

  async function saveWarehouse() {
    setWhError(null)
    if (!whForm.name.trim()) { setWhError('El nombre es obligatorio.'); return }
    setSavingWh(true)
    try {
      const created = await inv.createWarehouse({ name: whForm.name.trim(), ...(whForm.code.trim() ? { code: whForm.code.trim() } : {}) })
      setCreatingWh(false); setWhForm({ name: '', code: '' })
      warehouses.reload(); toast('Almacén creado', 'success')
      // Preselect newly-created warehouse if the create-item sheet is open.
      setCreateForm((f) => ({ ...f, warehouseId: f.warehouseId || created.id }))
    } catch (e) {
      setWhError(e instanceof Error ? e.message : 'No se pudo crear el almacén.')
    } finally { setSavingWh(false) }
  }

  const noWarehouses = warehouses.data != null && warehouses.data.length === 0
  const canCreate = (warehouses.data?.length ?? 0) > 0

  // Insumos sin costo (tag `needs-attention:cost-0`). Originados por la subida
  // del onboarding cuando el Excel ALMACEN traía #DIV/0!; el cliente necesita
  // saber qué insumos siguen sin precio antes de costear recetas.
  const needsAttention = useMemo(() => {
    return (ingredients.data ?? []).filter((p) => (p.tags ?? []).includes('needs-attention:cost-0'))
  }, [ingredients.data])

  // KPIs de la franja resumen: total / sin costo / bajo / agotado.
  const stats = useMemo(() => {
    const all = list.data ?? []
    return {
      total: all.length,
      noCost: all.filter((it) => it.unitCostCents == null || it.unitCostCents <= 0).length,
      low: all.filter((it) => it.status === 'LOW').length,
      out: all.filter((it) => it.status === 'OUT').length,
    }
  }, [list.data])

  // Lista filtrada (búsqueda + almacén) y ordenada en cliente.
  const rows = useMemo(() => {
    let r = list.data ?? []
    const q = search.trim().toLowerCase()
    if (q) r = r.filter((it) => it.name.toLowerCase().includes(q))
    if (whFilter) r = r.filter((it) => it.warehouseId === whFilter)
    if (sort) {
      const dir = sort.dir === 'asc' ? 1 : -1
      r = r.slice().sort((a, b) => {
        let av: number | string, bv: number | string
        switch (sort.key) {
          case 'name': av = a.name.toLowerCase(); bv = b.name.toLowerCase(); break
          case 'warehouse': av = whName(a.warehouseId).toLowerCase(); bv = whName(b.warehouseId).toLowerCase(); break
          case 'onHand': av = a.onHand; bv = b.onHand; break
          case 'available': av = a.onHand - a.reservedQty; bv = b.onHand - b.reservedQty; break
          case 'cost': av = a.unitCostCents ?? -1; bv = b.unitCostCents ?? -1; break
          default: return 0
        }
        return av < bv ? -dir : av > bv ? dir : 0
      })
    }
    return r
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, search, whFilter, sort, warehouses.data])

  const columns: DataTableColumn<inv.StockItemDetail>[] = [
    { key: 'name', header: 'Insumo / SKU', sortable: true, render: (it) => <span style={{ fontWeight: 'var(--pos-weight-medium, 500)' }}>{it.name}</span> },
    { key: 'warehouse', header: 'Almacén', sortable: true, render: (it) => whName(it.warehouseId) },
    { key: 'onHand', header: 'Existencia', align: 'right', sortable: true, render: (it) => <Qty value={it.onHand} unit={it.unit} /> },
    { key: 'available', header: 'Disponible', align: 'right', sortable: true, render: (it) => <Qty value={Math.round((it.onHand - it.reservedQty) * 100) / 100} unit={it.unit} /> },
    { key: 'cost', header: 'Costo', align: 'right', sortable: true, render: (it) => it.unitCostCents != null && it.unitCostCents > 0 ? <span className="adm-mono">{mxn(it.unitCostCents)}</span> : <StatusPill tone="neutral" label="Sin costo" /> },
    { key: 'estado', header: 'Estado', render: (it) => <StatusPill tone={STATUS_TONE[it.status]} label={STATUS_LABEL[it.status]} /> },
    {
      key: 'acciones', header: '', align: 'right', render: (it) => (
        <div style={{ display: 'inline-flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => openAdjust(it)} aria-label="Ajustar existencia" title="Ajustar a una cantidad exacta"><Calculator size={15} /></button>
          <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => remove(it)} aria-label="Eliminar"><Trash2 size={15} /></button>
        </div>
      ),
    },
  ]

  const whOptions = [{ value: '', label: 'Todos los almacenes' }, ...(warehouses.data ?? []).map((w) => ({ value: w.id, label: w.name }))]

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div><h1 className="adm-page__title">Existencias</h1><p className="adm-page__sub">Stock por almacén y mínimos de reorden</p></div>
        <div className="adm-page__actions">
          <Link className="adm-btn" to="/inventory/movements"><History size={16} /> Movimientos</Link>
          <Link className="adm-btn" to="/inventory/lots"><CalendarClock size={16} /> Lotes</Link>
          <Link className="adm-btn" to="/inventory/warehouses"><WarehouseIcon size={16} /> Almacenes</Link>
          <button className="adm-btn adm-btn--primary" onClick={openCreate} disabled={!canCreate} title={canCreate ? '' : 'Crea un almacén primero'}>
            <Plus size={16} /> Nuevo insumo
          </button>
        </div>
      </div>

      {list.data && (
        <div style={{ marginBottom: 16 }}>
          <SummaryStrip items={[
            { label: 'Insumos', value: stats.total },
            { label: 'Sin costo', value: stats.noCost, tone: stats.noCost ? 'warn' : 'neutral' },
            { label: 'Bajo mínimo', value: stats.low, tone: stats.low ? 'warn' : 'neutral' },
            { label: 'Agotados', value: stats.out, tone: stats.out ? 'danger' : 'neutral' },
          ]} />
        </div>
      )}

      {noWarehouses && (
        <div className="adm-banner adm-banner--warn" role="status" style={{ marginBottom: 12 }}>
          No hay almacenes configurados. Crea uno para empezar a registrar insumos.
          <button className="adm-btn adm-btn--sm" style={{ marginLeft: 8 }} onClick={() => setCreatingWh(true)}>Crear almacén</button>
        </div>
      )}

      {needsAttention.length > 0 && (
        <div className="adm-banner adm-banner--warn" role="status" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <AlertTriangle size={16} aria-hidden style={{ marginTop: 2, flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {needsAttention.length} insumo{needsAttention.length === 1 ? '' : 's'} sin costo — asígnale precio antes de costear recetas
              </div>
              <div className="adm-row__meta" style={{ marginBottom: 6 }}>
                Quedaron con precio $0 porque en el Excel del onboarding venían como <code>#DIV/0!</code>. Mientras no tengan costo, las recetas que los usan reportarán costo $0 (subestimado).
              </div>
              <details>
                <summary style={{ cursor: 'pointer' }}>Ver lista</summary>
                <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                  {needsAttention.map((p) => (
                    <li key={p.id} style={{ marginBottom: 2 }}>
                      <strong>{p.name}</strong> {p.sku ? <span style={{ color: 'var(--pos-fg-tertiary)' }}>· {p.sku}</span> : null}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          </div>
        </div>
      )}

      {asyncState(list.loading, list.error, list.reload)}

      {list.data && (
        <DataTable<inv.StockItemDetail>
          columns={columns}
          rows={rows}
          getRowKey={(it) => it.id}
          zebra
          sort={sort ?? null}
          onSortChange={setSort}
          onRowClick={(it) => navigate(`/inventory/items/${it.id}`)}
          emptyText={list.data.length === 0 ? 'Sin insumos' : 'Ningún insumo coincide con el filtro.'}
          toolbar={<FilterBar
            search={search}
            onSearch={setSearch}
            searchPlaceholder="Buscar insumo o SKU…"
            filters={[{ key: 'wh', label: 'Almacén', value: whFilter, onChange: setWhFilter, options: whOptions }]}
          />}
        />
      )}

      {/* Create */}
      <SectionedSheet
        open={creating}
        title="Nuevo insumo"
        onClose={() => { setCreating(false); setFormError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setCreating(false)}>Cancelar</button>
          <button
            className="adm-btn adm-btn--primary"
            onClick={saveCreate}
            disabled={saving || !createForm.warehouseId || (createForm.source === 'product' ? !createForm.productId : !createForm.ingredientName.trim()) || !createForm.unit.trim()}
          >{saving ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >
        {formError && <div className="adm-banner adm-banner--danger" role="alert">{formError}</div>}

        <FormSection title="Ubicación">
          <label className="adm-field">
            <span className="adm-field__label">Almacén</span>
            <select
              className="adm-select"
              value={createForm.warehouseId}
              onChange={(e) => setCreateForm({ ...createForm, warehouseId: e.target.value })}
              aria-label="Almacén"
            >
              <option value="">— Selecciona almacén —</option>
              {(warehouses.data ?? []).map((w) => <option key={w.id} value={w.id}>{w.name}{w.code ? ` (${w.code})` : ''}</option>)}
            </select>
          </label>
        </FormSection>

        <FormSection title="Insumo">
          <div role="group" aria-label="Origen del insumo" style={{ display: 'inline-flex', gap: 4 }}>
            <button type="button" className={`adm-btn adm-btn--sm${createForm.source === 'product' ? ' adm-btn--primary' : ' adm-btn--ghost'}`} onClick={() => setCreateForm({ ...createForm, source: 'product' })} aria-pressed={createForm.source === 'product'}>Producto del catálogo</button>
            <button type="button" className={`adm-btn adm-btn--sm${createForm.source === 'free' ? ' adm-btn--primary' : ' adm-btn--ghost'}`} onClick={() => setCreateForm({ ...createForm, source: 'free' })} aria-pressed={createForm.source === 'free'}>Nombre libre (materia prima)</button>
          </div>
          {createForm.source === 'product' ? (
            <label className="adm-field">
              <span className="adm-field__label">Producto</span>
              <select
                className="adm-select"
                value={createForm.productId}
                onChange={(e) => setCreateForm({ ...createForm, productId: e.target.value })}
                aria-label="Producto"
              >
                <option value="">— Selecciona producto —</option>
                {(products.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}{p.sku ? ` · ${p.sku}` : ''}</option>)}
              </select>
            </label>
          ) : (
            <label className="adm-field">
              <span className="adm-field__label">Nombre del insumo</span>
              <input className="adm-input" value={createForm.ingredientName} onChange={(e) => setCreateForm({ ...createForm, ingredientName: e.target.value })} placeholder="Café en grano, Servilletas…" />
            </label>
          )}
        </FormSection>

        <FormSection title="Medida, costo y reorden">
          <UnitPicker label="Unidad" value={createForm.unit} onChange={(unit) => setCreateForm({ ...createForm, unit })} />
          <label className="adm-field"><span className="adm-field__label">Costo unitario (MXN)</span><MoneyInput valueCents={createForm.unitCostCents} onChangeCents={(c) => setCreateForm({ ...createForm, unitCostCents: c })} /></label>
          <label className="adm-field"><span className="adm-field__label">Mínimo</span><input className="adm-input" type="number" min={0} value={createForm.reorderLevel} onChange={(e) => setCreateForm({ ...createForm, reorderLevel: Number(e.target.value) })} /></label>
        </FormSection>
      </SectionedSheet>

      {/* Ajustar a N (fijar existencia exacta) */}
      <SectionedSheet
        open={adjustingItem != null}
        title="Ajustar a una cantidad"
        onClose={() => { setAdjustingItem(null); setFormError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setAdjustingItem(null)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveAdjust} disabled={saving}>{saving ? 'Ajustando…' : 'Ajustar'}</button>
        </>}
      >
        {formError && <div className="adm-banner adm-banner--danger" role="alert">{formError}</div>}
        {adjustingItem && (
          <FormSection title="Nueva existencia">
            <p className="adm-row__meta" style={{ marginBottom: 0 }}>{adjustingItem.name} — existencia actual <span className="adm-mono">{adjustingItem.onHand} {unitShort(adjustingItem.unit)}</span></p>
            <label className="adm-field"><span className="adm-field__label">Fijar existencia a ({unitShort(adjustingItem.unit)})</span><input className="adm-input" type="number" min={0} value={adjustQty} onChange={(e) => setAdjustQty(Number(e.target.value))} autoFocus /></label>
            <p className="adm-row__meta">Se registrará un movimiento de ajuste de <span className="adm-mono">{Math.round((adjustQty - adjustingItem.onHand) * 100) / 100} {unitShort(adjustingItem.unit)}</span> (nuevo − actual).</p>
          </FormSection>
        )}
      </SectionedSheet>

      {/* Warehouse creation (empty-state CTA) */}
      <Sheet
        open={creatingWh}
        title="Nuevo almacén"
        onClose={() => { setCreatingWh(false); setWhError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setCreatingWh(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveWarehouse} disabled={savingWh || !whForm.name.trim()}>{savingWh ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >
        {whError && <div className="adm-banner adm-banner--danger" role="alert">{whError}</div>}
        <label className="adm-field"><span className="adm-field__label">Nombre</span><input className="adm-input" value={whForm.name} onChange={(e) => setWhForm({ ...whForm, name: e.target.value })} placeholder="Almacén principal" /></label>
        <label className="adm-field"><span className="adm-field__label">Código (opcional)</span><input className="adm-input" value={whForm.code} onChange={(e) => setWhForm({ ...whForm, code: e.target.value })} placeholder="MAIN" /></label>
      </Sheet>
    </div>
  )
}
